import { Language } from "@/lib/i18n/translations";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Mapping from English slugs to localized versions.
 * Only the first path segment is translated.
 */
export const SLUG_MAP: Record<string, Record<Language, string>> = {
  'pricing':  { en: 'pricing',  fr: 'tarifs',          es: 'precios' },
  'privacy':  { en: 'privacy',  fr: 'confidentialite', es: 'privacidad' },
  'terms':    { en: 'terms',    fr: 'conditions',      es: 'terminos' },
  'docs':     { en: 'docs',     fr: 'documentation',   es: 'documentacion' },
  'auth':     { en: 'auth',     fr: 'connexion',       es: 'iniciar-sesion' },
  'account':  { en: 'account',  fr: 'compte',          es: 'cuenta' },
  'projects': { en: 'projects', fr: 'projets',         es: 'proyectos' },
  'shared':   { en: 'shared',   fr: 'partage',         es: 'compartido' },
  'contact':  { en: 'contact',  fr: 'nous-joindre',    es: 'contacto' },
  'reset-password': { en: 'reset-password', fr: 'reinitialiser-mot-de-passe', es: 'restablecer-contrasena' },
  'generators': { en: 'generators', fr: 'generateurs', es: 'generadores' },
  'structured-data': { en: 'structured-data', fr: 'donnees-structurees', es: 'datos-estructurados' },
};

// Reverse map: for each language, maps localized slug → English slug
const REVERSE_SLUG_MAP: Record<Language, Record<string, string>> = { en: {}, fr: {}, es: {} };
for (const [enSlug, translations] of Object.entries(SLUG_MAP)) {
  for (const lang of ['fr', 'es'] as const) {
    REVERSE_SLUG_MAP[lang][translations[lang]] = enSlug;
  }
}

/**
 * Translate the first path segment from localized → English.
 */
function delocalizeFirstSegment(path: string, lang: Language): string {
  if (lang === 'en' || path === '/') return path;
  const segments = path.split('/');
  // segments[0] is '' (before leading /)
  if (segments.length > 1 && REVERSE_SLUG_MAP[lang][segments[1]]) {
    segments[1] = REVERSE_SLUG_MAP[lang][segments[1]];
  }
  return segments.join('/');
}

/**
 * Translate the first path segment from English → localized.
 */
function localizeFirstSegment(path: string, lang: Language): string {
  if (lang === 'en' || path === '/') return path;
  const segments = path.split('/');
  if (segments.length > 1 && SLUG_MAP[segments[1]]) {
    segments[1] = SLUG_MAP[segments[1]][lang];
  }
  return segments.join('/');
}

/**
 * Strip language prefix AND de-localize slugs to get the base English path.
 */
export function stripLangPrefix(pathname: string): string {
  let lang: Language = 'en';
  let path = pathname;

  if (pathname.startsWith('/fr/') || pathname === '/fr') {
    lang = 'fr';
    path = pathname.slice(3) || '/';
  } else if (pathname.startsWith('/es/') || pathname === '/es') {
    lang = 'es';
    path = pathname.slice(3) || '/';
  }

  return delocalizeFirstSegment(path, lang);
}

/**
 * Add a language prefix AND localize slugs.
 * English (default) has no prefix and keeps English slugs.
 */
export function localizePathname(path: string, lang: Language): string {
  if (lang === 'en') return path;

  // Handle hash-only paths like "#faq"
  if (path.startsWith('#')) return path;

  // Split hash
  const hashIdx = path.indexOf('#');
  const hashPart = hashIdx >= 0 ? path.slice(hashIdx) : '';
  const withoutHash = hashIdx >= 0 ? path.slice(0, hashIdx) : path;

  // Split query
  const queryIdx = withoutHash.indexOf('?');
  const queryPart = queryIdx >= 0 ? withoutHash.slice(queryIdx) : '';
  const cleanPath = queryIdx >= 0 ? withoutHash.slice(0, queryIdx) : withoutHash;

  // Localize the slug
  const localizedPath = localizeFirstSegment(cleanPath, lang);

  if (localizedPath === '/') return `/${lang}${queryPart}${hashPart}`;
  return `/${lang}${localizedPath}${queryPart}${hashPart}`;
}

/**
 * Build a full URL for a given language and base (English) path.
 * Used by SEOHead for hreflang tags.
 */
export function buildLocalizedUrl(basePath: string, lang: Language, baseUrl: string): string {
  const localizedPath = localizePathname(basePath, lang);
  return `${baseUrl}${localizedPath}`;
}

/**
 * Get the current language prefix for use in URLs.
 */
export function getLangPrefix(lang: Language): string {
  return lang === 'en' ? '' : `/${lang}`;
}

/**
 * Hook that returns a function to localize paths based on current language.
 */
export function useLocalePath() {
  const { language } = useLanguage();
  return (path: string) => localizePathname(path, language);
}

/**
 * Detect language from a URL pathname.
 */
export function detectLangFromPath(pathname: string): Language {
  if (pathname.startsWith('/fr/') || pathname === '/fr') return 'fr';
  if (pathname.startsWith('/es/') || pathname === '/es') return 'es';
  return 'en';
}
